import { type MutationMeta, useMutation } from '@tanstack/react-query';

import { type CmykShade, endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import { brandHubQueryKey, byName } from '@/features/brandHub/brandHub';
import { type LiveCollection, useLiveCollection, useLiveMutation } from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import { markPendingWrite, type PendingKind, scopeOf } from '@/lib/pendingWrites';
import { parseWriteResponse, requireParsed } from '@/lib/readers';
import { type Brand, type BrandColour, parseBrand, shadeIdOf } from './types';

const BRAND_LIMIT = 200;

/** Returns every active brand of the organization, sorted by name. */
export function useBrands(): LiveCollection<Brand> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<Brand>({
    path: collections.brands(organizationId ?? '_'),
    httpPath: endpoints.brandHub.brands(),
    queryKey: brandHubQueryKey('brands', organizationId),
    parse: parseBrand,
    limit: BRAND_LIMIT,
    enabled: Boolean(organizationId),
  });

  const data = byName(
    collection.data.filter((brand) => brand.isActive),
    (brand) => brand.name,
  );
  return { ...collection, data };
}

/** Returns one brand by its slug, from the same data as the list. */
export function useBrand(slug: string) {
  const brands = useBrands();
  return { ...brands, brand: brands.data.find((brand) => brand.id === slug) };
}

/** Returns every tag in use, sorted, for a filter to offer. */
export function useAvailableTags(rows: { tags: string[] }[]): string[] {
  return [...new Set(rows.flatMap((row) => row.tags))].sort((left, right) => left.localeCompare(right));
}

/** Returns the rows whose name contains the query and that have a selected tag. */
export function useFilteredByNameAndTags<T extends { name: string; tags: string[] }>(
  rows: T[],
  query: string,
  tags: readonly string[],
): T[] {
  const needle = query.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!needle || row.name.toLowerCase().includes(needle)) &&
      (tags.length === 0 || row.tags.some((tag) => tags.includes(tag))),
  );
}

export interface BrandFontAdd {
  family: string;
  objectName: string | null;
}

export interface BrandCreate {
  name: string;
  tags: string[];
}

/** An absent field is unchanged. */
export interface BrandUpdate {
  name?: string;
  tags?: string[];
}

/** Sends one brand request and puts the brand in the response into the list, marked
 *  until the stored data shows it. */
function useBrandMutation<TVariables>(
  kind: PendingKind,
  send: (variables: TVariables) => Promise<unknown>,
  onSuccess?: (brand: Brand, variables: TVariables) => void,
  meta?: MutationMeta,
  onError?: (error: Error, variables: TVariables) => void,
) {
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: brandHubQueryKey('brands', organizationId),
    kind,
    mutationFn: async (variables: TVariables) =>
      requireParsed(parseWriteResponse(await send(variables), 'slug', parseBrand), 'brand'),
    rowOf: (brand: Brand) => brand,
    onSuccess,
    onError,
    meta,
  });
}

/** Returns the name the writes of one array on the brand share. */
export function useBrandItemScope(slug: string, field: 'fonts' | 'extractedColours'): string {
  const organizationId = useActiveOrganizationId();
  return scopeOf(brandHubQueryKey('brands', organizationId, slug, field));
}

/** Stores one brand as an empty draft; the wizard adds its material afterwards. */
export function useCreateBrand() {
  return useBrandMutation(
    'create',
    (brand: BrandCreate) =>
      apiClient.post(endpoints.brandHub.brand.create(), {
        name: brand.name,
        tags: brand.tags,
      }),
    undefined,
    { toastsError: false },
  );
}

/** Changes the name or the tags of one brand. */
export function useUpdateBrand() {
  return useBrandMutation('update', ({ slug, update }: { slug: string; update: BrandUpdate }) =>
    apiClient.put(endpoints.brandHub.brand.update(slug), {
      ...(update.name !== undefined && { name: update.name }),
      ...(update.tags !== undefined && { tags: update.tags }),
    }),
  );
}

/** Replaces the whole palette. */
export function useSetBrandColours() {
  return useBrandMutation('update', ({ slug, colours }: { slug: string; colours: BrandColour[] }) =>
    apiClient.put(endpoints.brandHub.brand.colours(slug), {
      colours: colours.map((colour) => ({
        label: colour.label,
        cyan: colour.cyan,
        magenta: colour.magenta,
        yellow: colour.yellow,
        key: colour.key,
      })),
    }),
  );
}

/** Adds one font. Without an object name the backend fetches the Google file itself.
 *  `onError` gets each failure with the font that failed. */
export function useAddBrandFont(slug: string, onError?: (error: Error, font: BrandFontAdd) => void) {
  const scope = useBrandItemScope(slug, 'fonts');
  return useBrandMutation(
    'update',
    ({ family, objectName }: BrandFontAdd) =>
      apiClient.post(endpoints.brandHub.brand.fonts(slug), {
        family,
        ...(objectName && { object_name: objectName }),
      }),
    (brand, { family }) => {
      const font = brand.fonts.find((added) => added.family === family);
      if (font) markPendingWrite({ scope, id: font.fontId, kind: 'create', row: { ...font, id: font.fontId } });
    },
    { toastsError: false },
    onError,
  );
}

/** Removes one font and its file. */
export function useRemoveBrandFont(slug: string) {
  const scope = useBrandItemScope(slug, 'fonts');
  return useBrandMutation(
    'update',
    (fontId: string) => apiClient.delete(endpoints.brandHub.brand.font(slug, fontId)),
    (_body, fontId) => markPendingWrite({ scope, id: fontId, kind: 'delete' }),
  );
}

/** Removes one proposed colour from the extraction of this brand. */
export function useRejectExtractedColour(slug: string) {
  const scope = useBrandItemScope(slug, 'extractedColours');
  return useBrandMutation(
    'update',
    (values: CmykShade) => apiClient.delete(endpoints.brandHub.brand.extractedColour(slug, values)),
    (_body, values) => markPendingWrite({ scope, id: shadeIdOf(values), kind: 'delete' }),
    { errorKey: 'brandHub.colour.rejectFailed' },
  );
}

/** Asks the backend to read the palette from the guideline documents. Firestore gives the result. */
export function useRequestColourExtraction() {
  return useMutation({
    mutationFn: (slug: string) =>
      apiClient.post(endpoints.brandHub.brand.extractColours(slug)),
  });
}

/** Deactivates the brand, then calls `onDeactivated`, also when the caller has unmounted.
 *  Its files and products stay; it leaves the list. */
export function useDeactivateBrand({ onDeactivated }: { onDeactivated?: () => void } = {}) {
  return useBrandMutation(
    'delete',
    (slug: string) => apiClient.delete(endpoints.brandHub.brand.deactivate(slug)),
    onDeactivated,
  );
}
